import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

export interface PageHeaderProps {
  title: string;
  description?: ReactNode;
  /** Buttons for the page — rendered right-aligned, wrapping on small screens. */
  actions?: ReactNode;
  /** Chips or status shown directly beneath the title. */
  meta?: ReactNode;
}

/**
 * The standard page heading.
 *
 * Deliberately has no breadcrumbs: the trail lives in the app bar and is rendered
 * once by AppShell from the route
 * (docs/architecture/navigation-and-breadcrumbs.md §2). A page that drew its own
 * would show the user two trails that could disagree.
 */
export function PageHeader({ title, description, actions, meta }: PageHeaderProps) {
  return (
    <Box sx={{ mb: 3 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'flex-start' }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h4" component="h1">
            {title}
          </Typography>
          {description ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {description}
            </Typography>
          ) : null}
          {meta ? (
            <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', gap: 1 }}>
              {meta}
            </Stack>
          ) : null}
        </Box>
        {actions ? (
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            {actions}
          </Stack>
        ) : null}
      </Stack>
    </Box>
  );
}
